import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AllCommunityModule, DefaultMenuItem, ModuleRegistry, RowClickedEvent, RowStyle } from 'ag-grid-community';
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  RowGroupingModule,
  SideBarModule,
  TreeDataModule,
} from 'ag-grid-enterprise';
import { getProductColDefs } from './privateBankingOverviewColDefs';
import { getDefaultVisibleStatuses, getStatusFilterValues } from './defaultVisibleStatuses';
import PrivateBankingProductDetails from './PrivateBankingProductDetails';
import { PrivateBankingError } from './PrivateBankingError';
import { OpenDocumentsInCrmButton } from './OpenDocumentsInCrmButton';
import {
  ZfBox,
  ZfIcon,
  ZfInput,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfTooltip,
  ZfTypography,
} from '@zafire/react';
import {
  fetchServiceAgreementDetails,
  getServiceAgreementDetailsQueryKey,
  SERVICE_AGREEMENT_LIST_QUERY_KEY,
  useGetServiceAgreementDetails,
  useGetServiceAgreementList,
} from '../../hooks/queries/privateBanking';
import { useProfilingWizardStatus } from '../../hooks/queries/navigationWizardStatus';
import {
  clearSelectedPBProduct,
  selectSelectedPBProduct,
  setSelectedPBProduct,
} from '../../reducers/privateBankingReducer';
import { useUserPermissions } from '../../hooks/queries/userPermissions';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../reducers/modalReducer';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { EmptyStateOverlay } from './EmptyStateOverlay';
import { ServiceAgreementV2 } from './eBankingTempTypes';
import { getProductDisplayName, mapServiceAgreementsToTreeRows } from './mappers/serviceAgreementToTreeMapper';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useBusinessRelationTypeGate } from '../../hooks/queries/businessRelationType';
import {
  InvestmentProductStatus,
  useModularOfferingFastTrackElement,
} from '../../hooks/useModularOfferingFastTrackElement';
import { FeatureFlagName } from 'services/portfolios/portfolioService';
import useGetFeatureFlags from 'components/portfolio/hooks/useGetFeatureFlags';
import { showToast } from 'reducers/toastReducer';
import './PrivateBankingOverview.css';

ModuleRegistry.registerModules([
  AllCommunityModule,
  RowGroupingModule,
  TreeDataModule,
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
]);

type RouteParams = {
  brUuid: string;
};

const sortingMenuItems: DefaultMenuItem[] = ['sortAscending', 'sortDescending'];

export const PrivateBankingOverview: React.FC = () => {
  const { t } = useTranslation();
  const { brUuid } = useParams<RouteParams>();
  const { isPrivateBankingBr, isBrTypeUnavailable, isWaitingForCurrentBrType, error } =
    useBusinessRelationTypeGate(brUuid);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInvestmentDialog, setShowInvestmentDialog] = useState(false);
  const investmentProductRef = useRef<HTMLElement>(null);
  const bankingProductsButtonRef = useRef<HTMLSpanElement | null>(null);
  const {
    data: serviceAgreementList,
    isFetching,
    isLoading,
    isError: isServiceAgreementListError,
    error: serviceAgreementListError,
  } = useGetServiceAgreementList({
    businessRelationUid: brUuid,
  });

  const serviceAgreements = useMemo(() => serviceAgreementList?.list ?? [], [serviceAgreementList?.list]);

  const queryClient = useQueryClient();
  useProfilingWizardStatus(brUuid, serviceAgreementList?.list);

  const rowData = useMemo(
    () => mapServiceAgreementsToTreeRows(serviceAgreements as ServiceAgreementV2[]),
    [serviceAgreements],
  );
  const { data: featureFlag, isLoading: featureFlagLoading } = useGetFeatureFlags();
  const dispatch = useDispatch();
  const { data: userPermissions, isPending: arePermissionsPending } = useUserPermissions();
  const canManageAgreements = userPermissions?.canManageAgreements ?? false;
  const isManageAgreementsRestricted = !arePermissionsPending && !canManageAgreements;
  const selectedPBProduct = useSelector(selectSelectedPBProduct);
  const selectedServiceAgreementId = selectedPBProduct?.serviceAgreementId;
  const {
    data: selectedProductDetails,
    isFetching: isProductDetailsFetching,
    isError: isProductDetailsError,
    error: productDetailsError,
  } = useGetServiceAgreementDetails(selectedServiceAgreementId);
  const { showErrorModal } = useErrorModal();

  const isInvalidPrivateBankingBr = !isWaitingForCurrentBrType && !isPrivateBankingBr;
  const errorMessage = !brUuid
    ? t(
        'pb.error.missingbr',
        'No business relationship was provided. Please try again with a valid private banking business relationship.',
      )
    : isBrTypeUnavailable
      ? t('pb.error.brunavailable', 'The business relationship could not be loaded. Please try again later.')
      : t('pb.error.notprivatebanking', 'This business relationship is not a private banking BR.');

  useEffect(() => {
    if (!isInvalidPrivateBankingBr) {
      return;
    }

    showErrorModal(error, {
      title: t('pb.error.modal.title', 'Private banking overview not available'),
      userErrorMessage: errorMessage,
    });
  }, [isInvalidPrivateBankingBr, errorMessage, error, showErrorModal, t]);

  useEffect(() => {
    if (isServiceAgreementListError) {
      showErrorModal(serviceAgreementListError, {
        title: t('pb.error.productlist.title', 'Error loading products'),
        userErrorMessage: t(
          'pb.error.productlist.message',
          'An error occurred when loading the products. Please try again later.',
        ),
        details: serviceAgreementListError?.message,
      });
    }
  }, [isServiceAgreementListError, serviceAgreementListError, showErrorModal, t]);

  useEffect(() => {
    if (!isProductDetailsError) {
      return;
    }

    showErrorModal(productDetailsError, {
      title: t('pb.error.productdetails.title', 'Error loading product details'),
      userErrorMessage: t(
        'pb.error.productdetails.message',
        'An error occurred when loading product details. Please try again later.',
      ),
      details: productDetailsError?.message,
    });
  }, [isProductDetailsError, productDetailsError, showErrorModal, t]);

  const showInvestmentProduct = useMemo(() => {
    if (!featureFlagLoading) {
      return featureFlag?.find((item) => item.name === FeatureFlagName.PORTFOLIO_CREATE_API_ENABLED)?.enabled ?? false;
    }

    return false;
  }, [featureFlag, featureFlagLoading]);

  const status = useModularOfferingFastTrackElement(showInvestmentProduct);

  const investmentProduct = useMemo(() => {
    if (!showInvestmentProduct) {
      return null;
    }
    if (
      status === InvestmentProductStatus.LOADING ||
      status === InvestmentProductStatus.ERROR ||
      !canManageAgreements
    ) {
      return (
        <span ref={investmentProductRef} style={{ display: 'inline-block' }}>
          <ZfTextButton size="medium" hierarchy="secondary" disabled>
            <ZfIcon slot="leading" name="add_circle" size="large" />
            {t('pb.investmentproduct.title')}
          </ZfTextButton>
          {isManageAgreementsRestricted && (
            <ZfTooltip
              text={t('pb.overview.actions.addinvestmentproducts.restricted.tooltip')}
              anchor={() => investmentProductRef.current as HTMLElement}
              side="top"
            />
          )}
        </span>
      );
    }
    return (
      <>
        <ZfTextButton size="medium" hierarchy="secondary" onClick={() => setShowInvestmentDialog(true)}>
          <ZfIcon slot="leading" name="add_circle" size="large" />
          {t('pb.investmentproduct.title')}
        </ZfTextButton>
        <iap-modular-offering-fast-track-element
          brUid={brUuid || ''}
          showDialog={showInvestmentDialog}
          ref={investmentProductRef}
        />
      </>
    );
  }, [status, showInvestmentProduct, brUuid, t, canManageAgreements, showInvestmentDialog]);

  useEffect(() => {
    if (!showInvestmentProduct || status !== InvestmentProductStatus.DONE) return;
    const element = investmentProductRef.current;
    if (!element) return;

    const handleSubmitted = () => {
      void queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] });
      setShowInvestmentDialog(false);
    };

    const handleCancel = () => {
      setShowInvestmentDialog(false);
    };

    element.addEventListener('FAST_TRACK_SUBMITTED', handleSubmitted);
    element.addEventListener('FAST_TRACK_CANCELLED', handleCancel);

    return () => {
      element.removeEventListener('FAST_TRACK_SUBMITTED', handleSubmitted);
      element.removeEventListener('FAST_TRACK_CANCELLED', handleCancel);
    };
  }, [queryClient, showInvestmentProduct, status]);

  const handleRowClick = useCallback(
    (event: RowClickedEvent) => {
      if ((event.event?.target as HTMLElement)?.closest('[data-no-row-click]')) return;
      const row = event.data as ServiceAgreementV2;
      if (!row) return;

      if (selectedPBProduct?.serviceAgreementId === row.serviceAgreementId) {
        dispatch(clearSelectedPBProduct());
        event.node.setSelected(false);
        return;
      }
      dispatch(setSelectedPBProduct(row as ServiceAgreementV2));
    },
    [dispatch, selectedPBProduct],
  );

  const handleOpenAddBankingProductsModal = () => {
    dispatch(
      openModal({
        modalType: 'AddBankingProductsModal',
        modalProps: {
          brUuid: brUuid,
        },
      }),
    );
  };

  const handleOpenInvestmentProductModal = useCallback(
    (serviceAgreementId: string) => {
      dispatch(
        openModal({
          modalType: 'AddPortfolioDialog',
          modalProps: {
            brUid: brUuid,
            serviceAgreementId,
            onProceed: async () => {
              dispatch(
                showToast({
                  type: 'success',
                  position: 'top-center',
                  titleText: t('pb.portfolio.added.toast.title'),
                  dismissible: true,
                  autoDismissDuration: 5000,
                }),
              );
              await queryClient.invalidateQueries({ queryKey: [SERVICE_AGREEMENT_LIST_QUERY_KEY] });
            },
          },
        }),
      );
    },
    [brUuid, dispatch, queryClient, t],
  );

  const handleOpenEditProductModal = useCallback(
    async (serviceAgreement: ServiceAgreementV2) => {
      let serviceAgreementForEdit = serviceAgreement;
      const serviceAgreementUid = serviceAgreement.serviceAgreementId;
      const currentServiceAgreements =
        queryClient.getQueryData<{ list?: ServiceAgreementV2[] }>([
          'serviceAgreementList',
          { businessRelationUid: brUuid },
        ])?.list ?? serviceAgreements;
      const relatedServiceAgreements = currentServiceAgreements.filter(
        (candidate) => candidate.parentAgreement === serviceAgreementUid,
      );
      const relatedServiceAgreementNames = relatedServiceAgreements
        .map(getProductDisplayName)
        .filter((name): name is string => Boolean(name));
      const relatedServiceAgreementUids = relatedServiceAgreements
        .map((candidate) => candidate.serviceAgreementId)
        .filter((uid): uid is string => Boolean(uid));

      if (serviceAgreementUid) {
        try {
          serviceAgreementForEdit = (await queryClient.fetchQuery({
            queryKey: getServiceAgreementDetailsQueryKey(serviceAgreementUid),
            queryFn: () => fetchServiceAgreementDetails(serviceAgreementUid),
            staleTime: 0,
          })) as ServiceAgreementV2;
        } catch (error) {
          showErrorModal(error, {
            title: 'Error loading product details',
            userErrorMessage: 'An error occurred when loading product details. Please try again later.',
          });
          return;
        }
      }

      dispatch(
        openModal({
          modalType: 'ConfigureProductModal',
          modalProps: {
            serviceAgreement: serviceAgreementForEdit,
            brUid: brUuid,
            relatedServiceAgreementNames,
            relatedServiceAgreementUids,
          },
        }),
      );
    },
    // Prefer the query cache over the list captured by this callback, while keeping the callback
    // stable when service-agreement data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brUuid, dispatch, queryClient, showErrorModal],
  );

  const statusesInData = serviceAgreements.map((sa) => sa.agreementStatus);

  const statusFilterValues = getStatusFilterValues(statusesInData).sort().join(',');

  const columnDefs = useMemo(
    () =>
      getProductColDefs({
        onEditProduct: handleOpenEditProductModal,
        onConfigureInvestmentProduct: handleOpenInvestmentProductModal,
        statusFilterValues: statusFilterValues.split(','),
        productTypeHeaderName: t('pb.overview.producttype.label'),
      }),
    // AG Grid treats a new columnDefs reference as a column reset. This explicit memo keeps the
    // grid intact while the agreement query polls INITIATED products for their next status.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [handleOpenEditProductModal, handleOpenInvestmentProductModal, statusFilterValues, t],
  );

  const getRowStyle = useCallback((): RowStyle => ({ cursor: 'pointer' }), []);

  const defaultVisibleStatuses = getDefaultVisibleStatuses(statusesInData);

  if (isInvalidPrivateBankingBr) {
    return <PrivateBankingError message={errorMessage} />;
  }

  return (
    <ZfBox
      style={{
        width: '100%',
        height: '100dvh',
        boxSizing: 'border-box',
        background: 'var(--bg-color-default-muted, #F8F9FB)',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <ZfBox
        style={{
          width: selectedPBProduct ? '67%' : '100%',
          height: '100%',
          boxSizing: 'border-box',
          padding: 'var(--padding-vertical-xl, 24px)',
          transition: '0.1s ease',
        }}
      >
        <ZfBox
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--gap-vertical-lg, 20px)',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            background: 'white',
            border: '1px solid var(--border-color-default-normal, #E0E1EB)',
            boxSizing: 'border-box',
            padding: '24px',
          }}
        >
          <ZfStack
            direction="column"
            spacing="small"
            style={{
              marginBottom: 'var(--gap-vertical-xxs, 4px)',
            }}
          >
            <ZfTypography variant="title-large" role={'heading'}>
              {t('pb.overview.products.title')}
            </ZfTypography>
            <ZfBox
              style={{
                display: 'flex',
                flexDirection: 'row-reverse',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--gap-vertical-sm, 12px)',
                width: '100%',
              }}
            >
              <ZfStack
                direction="row"
                spacing="small"
                style={{ justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <OpenDocumentsInCrmButton brUuid={brUuid} />
                {investmentProduct}
                <span ref={bankingProductsButtonRef} style={{ display: 'inline-block' }}>
                  <ZfTextButton
                    size="medium"
                    hierarchy="secondary"
                    disabled={!canManageAgreements}
                    onClick={handleOpenAddBankingProductsModal}
                  >
                    <ZfIcon slot="leading" name="add_circle" size="large" />
                    {t('pb.overview.banking.products.label')}
                  </ZfTextButton>
                </span>
                {isManageAgreementsRestricted && (
                  <ZfTooltip
                    text={t('pb.overview.actions.addbankingproducts.restricted.tooltip')}
                    anchor={() => bankingProductsButtonRef.current as HTMLElement}
                    side="top"
                  />
                )}
              </ZfStack>

              <ZfBox style={{ flex: '1 1 280px', minWidth: '240px', maxWidth: '33%', marginRight: 'auto' }}>
                <ZfInput
                  data-testid="banking-product-overview-search-input"
                  type="search"
                  value={searchTerm}
                  clearable
                  showSubmit={false}
                  placeholder="Search products"
                  onInputChange={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
                  onSearchSubmit={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
                />
              </ZfBox>
            </ZfBox>
          </ZfStack>

          <div
            className="private-banking-products-grid"
            style={{ flex: 1, minHeight: 0, height: '100%', width: '100%', boxSizing: 'border-box' }}
            data-testid={'AgGrid-container'}
          >
            {isWaitingForCurrentBrType || isLoading ? (
              <ZfBox style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <ZfLoadingSpinner size="medium" />
              </ZfBox>
            ) : (
              <AgGridReact
                initialState={{
                  filter: {
                    filterModel: {
                      agreementStatus: {
                        filterType: 'set',
                        values: defaultVisibleStatuses,
                      },
                    },
                  },
                }}
                rowData={rowData}
                quickFilterText={searchTerm}
                treeData
                treeDataDisplayType="custom"
                getDataPath={(row) => row._path}
                getRowId={(params) => params.data.serviceAgreementId}
                groupDefaultExpanded={-1}
                suppressCellFocus={true}
                columnDefs={columnDefs}
                getMainMenuItems={(params) => (params.column?.getColDef().sortable === false ? [] : sortingMenuItems)}
                onRowClicked={handleRowClick}
                getRowStyle={getRowStyle}
                sideBar={{
                  toolPanels: ['columns', 'filters'],
                  position: 'left',
                }}
                pagination={true}
                paginateChildRows={true}
                noRowsOverlayComponent={EmptyStateOverlay}
                noRowsOverlayComponentParams={{
                  title: t('pb.overview.emptystate.title', 'Start by adding a product or service'),
                  description: t(
                    'pb.overview.emptystate.description',
                    'Added products and services will be displayed here',
                  ),
                }}
                loading={isFetching}
                loadingOverlayComponent={ZfLoadingSpinner}
                cellSelection={false}
                suppressPaginationPanel={false}
                suppressRowHoverHighlight={false}
                theme={themeZafire}
                rowClassRules={{
                  'ag-row-selected': (params) =>
                    params.data?.serviceAgreementId === selectedPBProduct?.serviceAgreementId,
                }}
              />
            )}
          </div>
        </ZfBox>
      </ZfBox>

      {selectedPBProduct && (
        <ZfBox
          style={{
            width: '33%',
            height: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            borderLeft: '1px solid #e0e0e0',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ZfBox style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {isProductDetailsFetching && !selectedProductDetails && (
              <ZfBox style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
                <ZfLoadingSpinner size="medium" />
              </ZfBox>
            )}
            {selectedProductDetails && <PrivateBankingProductDetails product={selectedProductDetails} />}
          </ZfBox>
        </ZfBox>
      )}
    </ZfBox>
  );
};
